import { useGetIssues } from '@/store/api/services/issues/issues';
import { useGetMerchantsQuery } from '@/store/api/services/merchants/merchants';
import { useUpsertCaseMutation } from '@/store/api/services/operations/cases/index';
import { useGetCaseEnumsQuery } from '@/store/api/services/resources/enums';
import { useGetUsersAthenaQuery } from '@/store/api/services/users/users';
import type { Issue } from '@/types';
import type { Case } from '@/types/case';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

interface EditCaseDetailsProps extends ModalChildrenProps {
  caseData: Case;
}

const EditCaseDetails = ({ hide, props }: EditCaseDetailsProps) => {
  const [upsertCase, { isLoading }] = useUpsertCaseMutation();

  // Helper to extract and normalize case data from props
  const getCaseData = () => {
    return props?.caseData as Case & {
      case_type?: string;
      type?: string;
      priority?: string;
      assigned_to?: string;
      assigned_user?: string;
      assigned_user_id?: string;
      description?: string;
      issue_id?: string;
      issue_ids?: string[];
    };
  };

  // Helper to get form data from case data
  const getFormDataFromCase = (currentCaseData: ReturnType<typeof getCaseData>) => {
    if (!currentCaseData) {
      return {
        status: 'inprogress',
        resolution_summary: '',
        type: '',
        priority: 'low',
        assigned_user_id: '',
        merchant_id: '',
        issue_ids: [],
        description: '',
      };
    }

    // Handle issue_ids - can be an array or a single issue_id value
    let normalizedIssueIds: string[] = [];
    if (Array.isArray(currentCaseData.issue_ids)) {
      normalizedIssueIds = currentCaseData.issue_ids.map((id) => String(id));
    } else if (currentCaseData.issue_id) {
      normalizedIssueIds = [String(currentCaseData.issue_id)];
    }

    // Transform API values to match dropdown formats:
    // - Status: remove underscores and lowercase (e.g., "in_progress" -> "inprogress")
    // - Type: capitalize first letter (e.g., "machine" -> "Machine")
    const apiStatus = currentCaseData.status || 'in_progress';
    const normalizedStatus = apiStatus.toLowerCase().replace(/_/g, '');

    const apiType = currentCaseData.type || currentCaseData.case_type || '';
    const normalizedType = apiType ? apiType.charAt(0).toUpperCase() + apiType.slice(1) : '';

    // Normalize assigned_user_id to string
    const assignedUserId =
      currentCaseData.assigned_user_id ||
      currentCaseData.assigned_to ||
      currentCaseData.assigned_user ||
      '';
    const normalizedAssignedUserId = assignedUserId ? String(assignedUserId) : '';

    // Normalize merchant_id to string
    const merchantId = currentCaseData.merchant_id || '';
    const normalizedMerchantId = merchantId ? String(merchantId) : '';

    return {
      status: normalizedStatus,
      resolution_summary: currentCaseData.resolution_summary || '',
      type: normalizedType,
      priority: currentCaseData.priority || 'low',
      assigned_user_id: normalizedAssignedUserId,
      merchant_id: normalizedMerchantId,
      issue_ids: normalizedIssueIds,
      description: currentCaseData.description || '',
    };
  };

  // Get caseData
  const caseData = getCaseData();

  // Compute form data from caseData - this will update when caseData changes
  const computedFormData = useMemo(() => {
    console.log('Computing form data from caseData:', {
      id: caseData?.id,
      issue_id: caseData?.issue_id,
      issue_ids: caseData?.issue_ids,
      fullCaseData: caseData,
    });
    const result = getFormDataFromCase(caseData);
    console.log('Computed form data (issue_ids):', result.issue_ids);
    return result;
  }, [
    caseData?.id,
    caseData?.status,
    caseData?.type,
    caseData?.priority,
    caseData?.resolution_summary,
    caseData?.issue_ids,
    caseData?.issue_id,
  ]);

  const [formData, setFormData] = useState(computedFormData);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [issuesInputValue, setIssuesInputValue] = useState('');
  const [debouncedIssuesSearchText, setDebouncedIssuesSearchText] = useState('');

  // Fetch users with Machine Dispatch/Tech role (role_id = 5) from athena
  const { data: usersData } = useGetUsersAthenaQuery({ role_id: 5 });
  const users = usersData?.data || [];

  // Fetch case enums for status, priority, type, record_status
  const { data: caseEnumsData } = useGetCaseEnumsQuery();

  // Debounce issues search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedIssuesSearchText(issuesInputValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [issuesInputValue]);

  // Fetch issues for the dropdown (server-side search)
  const shouldFetchIssues = issuesOpen || debouncedIssuesSearchText.trim().length > 0;
  const { data: issuesData, isFetching: isLoadingIssues } = useGetIssues(
    shouldFetchIssues
      ? {
          search_text: debouncedIssuesSearchText || undefined,
          pagination: {
            current_page: 1,
            items_per_page: 50,
            sort_by: 'detected_at',
            sort_ascending: false,
          },
        }
      : skipToken,
  );

  // Also fetch issues linked to this specific case
  const { data: linkedIssuesData } = useGetIssues(
    caseData?.id
      ? {
          filter: {
            operator: 'equal',
            property: 'linked_case_id',
            value: caseData.id,
          },
          pagination: {
            current_page: 1,
            items_per_page: 1000,
            sort_by: 'detected_at',
            sort_ascending: false,
          },
        }
      : skipToken,
  );
  const linkedIssues = linkedIssuesData?.data || [];

  const issueOptions = useMemo(() => {
    const issuesMap = new Map<string, Issue>();
    (issuesData?.data || []).forEach((issue) => {
      issuesMap.set(String(issue.id), issue);
    });
    linkedIssues.forEach((issue) => {
      issuesMap.set(String(issue.id), issue);
    });
    return Array.from(issuesMap.values());
  }, [issuesData, linkedIssues]);

  const issuesById = useMemo(() => {
    const map = new Map<string, Issue>();
    issueOptions.forEach((issue) => {
      map.set(String(issue.id), issue);
    });
    return map;
  }, [issueOptions]);

  const selectedIssues = useMemo(() => {
    return formData.issue_ids.map((id) => issuesById.get(String(id))).filter(Boolean) as Issue[];
  }, [formData.issue_ids, issuesById]);

  // Update formData when computedFormData changes or when linked issues are loaded
  useEffect(() => {
    console.log('EditCaseDetails - computedFormData changed:', computedFormData);
    const updatedFormData = { ...computedFormData };

    // If issue_ids is empty but we have linked issues, populate from linked issues
    if (updatedFormData.issue_ids.length === 0 && linkedIssues.length > 0) {
      updatedFormData.issue_ids = linkedIssues.map((issue) => String(issue.id));
      console.log('Populated issue_ids from linked issues:', updatedFormData.issue_ids);
    }

    setFormData(updatedFormData);
  }, [computedFormData, linkedIssues]);

  // Fetch merchants for merchant_id dropdown
  const { data: merchantsData } = useGetMerchantsQuery({
    pagination: {
      current_page: 1,
      items_per_page: 1000,
      sort_by: 'company_name',
      sort_ascending: true,
    },
  });
  const merchants = (merchantsData?.data || []).filter(
    (merchant) =>
      merchant?.company_name || (merchant?.owner_first_name && merchant?.owner_last_name),
  );

  // Check if case is closed or resolved (not updateable)
  const isCaseClosedOrResolved = caseData?.status === 'closed' || caseData?.status === 'resolved';

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const submitCaseData = getCaseData();
    if (!submitCaseData) {
      toast.error('No case data available');
      return;
    }

    // Prevent submission if case is closed or resolved
    if (isCaseClosedOrResolved) {
      toast.error('Cannot update a case that is closed or resolved');
      return;
    }

    try {
      // Transform form values back to API format:
      // - Status: convert "inprogress" -> "in_progress", "resolved" -> "resolved", etc.
      // - Type: convert "Machine" -> "machine", etc.
      const apiStatus = formData.status === 'inprogress' ? 'in_progress' : formData.status;
      const apiType = formData.type ? formData.type.toLowerCase() : undefined;

      // Map formData to API expected format
      const payload: any = {
        id: submitCaseData.id,
        status: apiStatus,
        resolution_summary: formData.resolution_summary || undefined,
        type: apiType,
        priority: formData.priority || undefined,
        assigned_user_id: formData.assigned_user_id || undefined,
        merchant_id: formData.merchant_id || undefined,
        issue_ids:
          formData.issue_ids && formData.issue_ids.length > 0 ? formData.issue_ids : undefined,
        description: formData.description || undefined,
      };

      // Remove undefined values
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === '') {
          delete payload[key];
        }
      });

      await upsertCase(payload).unwrap();

      toast.success('Case updated successfully');
      hide();
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message || error?.data?.message || error?.message || 'Error updating case';
      toast.error(errorMessage);
      console.error('Error updating case:', error);
    }
  };

  if (!caseData) {
    return null;
  }

  return (
    <>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Case Details
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Issue IDs (Multiple) */}
          <Grid item xs={12}>
            <Autocomplete
              multiple
              open={issuesOpen}
              onOpen={() => setIssuesOpen(true)}
              onClose={() => setIssuesOpen(false)}
              value={selectedIssues}
              onChange={(_, newValue) => {
                handleInputChange(
                  'issue_ids',
                  newValue.map((issue) => String(issue.id)),
                );
                setIssuesInputValue('');
              }}
              inputValue={issuesInputValue}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  setIssuesInputValue(newInputValue);
                }
              }}
              options={issueOptions}
              loading={isLoadingIssues}
              disabled={isCaseClosedOrResolved}
              filterOptions={(options) => options}
              getOptionLabel={(issue) => {
                const displayId = issue.display_id
                  ? `Issue ${issue.display_id}`
                  : `Issue ${issue.id}`;
                const atmLabel = issue.atm_id ? `ATM ${issue.atm_id}` : 'ATM N/A';
                return `${displayId} • ${issue.issue_type || 'Unknown'} • ${atmLabel}`;
              }}
              renderOption={(props, issue) => {
                const locationName =
                  issue.location?.location_name ||
                  issue.location?.street_address ||
                  (issue.location_id ? `Location ${issue.location_id}` : 'No Assigned Location');
                const detectedDate = issue.detected_at
                  ? new Date(issue.detected_at).toLocaleDateString()
                  : null;

                return (
                  <li {...props} key={issue.id}>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {issue.display_id ? `Issue ${issue.display_id}` : `Issue ${issue.id}`}
                        </Typography>
                        {issue.issue_type && (
                          <Typography variant="caption" color="text.secondary">
                            {issue.issue_type}
                          </Typography>
                        )}
                        {issue.severity && (
                          <Chip
                            label={issue.severity}
                            size="small"
                            color={
                              issue.severity === 'critical'
                                ? 'error'
                                : issue.severity === 'medium'
                                  ? 'warning'
                                  : 'default'
                            }
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {issue.status && (
                          <Chip
                            label={issue.status === 'active' ? 'Active' : 'Resolved'}
                            size="small"
                            color={issue.status === 'active' ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {issue.atm_id ? `ATM ${issue.atm_id}` : 'ATM N/A'}
                        {issue.location_id ? ` • Location ID ${issue.location_id}` : ''}
                        {locationName ? ` • ${locationName}` : ''}
                        {detectedDate ? ` • Detected ${detectedDate}` : ''}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderTags={() => null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Issues"
                  size="small"
                  placeholder={
                    selectedIssues.length === 0 ? 'Search issues by ID, type, or ATM' : ''
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingIssues ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={
                debouncedIssuesSearchText ? 'No issues found' : 'Type to search issues'
              }
            />
            {selectedIssues.length > 0 ? (
              <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedIssues.map((issue) => {
                  const label = issue.display_id
                    ? `Issue ${issue.display_id}`
                    : `Issue ${issue.id}`;
                  return (
                    <Chip
                      key={issue.id}
                      label={label}
                      size="small"
                      onDelete={
                        isCaseClosedOrResolved
                          ? undefined
                          : () =>
                              handleInputChange(
                                'issue_ids',
                                formData.issue_ids.filter((id) => String(id) !== String(issue.id)),
                              )
                      }
                    />
                  );
                })}
              </Box>
            ) : null}
          </Grid>

          {/* Status */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                disabled={isCaseClosedOrResolved}
              >
                {caseEnumsData?.data?.statuses?.map((status) => {
                  // Convert PascalCase/camelCase to snake_case, then lowercase
                  const normalizedStatus = status
                    .replace(
                      /([A-Z])/g,
                      (match, _, offset) => (offset > 0 ? '_' : '') + match.toLowerCase(),
                    )
                    .toLowerCase();
                  const displayLabel = normalizedStatus
                    .split('_')
                    .filter(Boolean)
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                  return (
                    <MenuItem key={status} value={normalizedStatus}>
                      {displayLabel}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Type */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => handleInputChange('type', e.target.value)}
                disabled={isCaseClosedOrResolved}
              >
                {caseEnumsData?.data?.types?.map((type) => {
                  // Convert PascalCase/camelCase to snake_case, then lowercase
                  const normalizedType = type
                    .replace(
                      /([A-Z])/g,
                      (match, _, offset) => (offset > 0 ? '_' : '') + match.toLowerCase(),
                    )
                    .toLowerCase();
                  const displayLabel = normalizedType
                    .split('_')
                    .filter(Boolean)
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                  return (
                    <MenuItem key={type} value={type}>
                      {displayLabel}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Priority */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => handleInputChange('priority', e.target.value)}
                disabled={isCaseClosedOrResolved}
              >
                {caseEnumsData?.data?.priorities?.map((priority) => (
                  <MenuItem key={priority} value={priority.toLowerCase()}>
                    {priority.charAt(0) + priority.slice(1).toLowerCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Assigned User */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Assigned User</InputLabel>
              <Select
                value={formData.assigned_user_id}
                label="Assigned User"
                onChange={(e) => handleInputChange('assigned_user_id', e.target.value)}
                disabled={isCaseClosedOrResolved}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={String(user.id)}>
                    {user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Merchant */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Merchant</InputLabel>
              <Select
                value={formData.merchant_id}
                label="Merchant"
                onChange={(e) => handleInputChange('merchant_id', e.target.value)}
                disabled={isCaseClosedOrResolved}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {merchants.map((merchant) => (
                  <MenuItem key={merchant.id} value={String(merchant.id)}>
                    {merchant.company_name ||
                      `${merchant.owner_first_name} ${merchant.owner_last_name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Issue Description
            </Typography>
            <TextareaAutosize
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              minRows={4}
              maxRows={8}
              placeholder={formData.description ? undefined : 'Enter issue description...'}
              disabled={isCaseClosedOrResolved}
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '12px 14px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                backgroundColor: isCaseClosedOrResolved ? 'rgba(0, 0, 0, 0.06)' : 'transparent',
                outline: 'none',
                cursor: isCaseClosedOrResolved ? 'not-allowed' : 'text',
              }}
            />
          </Grid>

          {/* Resolution Summary - Available for both types */}
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Resolution Summary
            </Typography>
            <TextareaAutosize
              value={formData.resolution_summary}
              onChange={(e) => handleInputChange('resolution_summary', e.target.value)}
              minRows={4}
              maxRows={12}
              placeholder={formData.resolution_summary ? undefined : 'Enter resolution details...'}
              disabled={isCaseClosedOrResolved}
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '12px 14px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                backgroundColor: isCaseClosedOrResolved ? 'rgba(0, 0, 0, 0.06)' : 'transparent',
                outline: 'none',
                cursor: isCaseClosedOrResolved ? 'not-allowed' : 'text',
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button onClick={hide} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || isCaseClosedOrResolved}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </>
  );
};

export default EditCaseDetails;
